import { z } from 'zod';

/**
 * JEDEN namespace KV, prefiksy zamiast osobnych bindingów.
 *
 * Kolejne etapy dołożą jeszcze kilka rodzajów kluczy efemerycznych (throttle
 * linków, liczniki rate limitu, cache JWKS). Namespace i binding per zastosowanie
 * to churn przez wrangler.jsonc, wygenerowane typy i konfigurację testów —
 * prefiks daje tę samą izolację bez tego kosztu.
 */
export const KV_PREFIX = {
  oauthState: 'oauth:state:',
  /** Sesja klienta końcowego po autoryzacji OAuth (Etap 3). */
  session: 'session:',
  /** Sesja zalogowanego pracownika. Jej istnienie decyduje o dostępie. */
  staffSession: 'staff:session:',
  managedRulesets: 'cf:managed-rulesets:',
} as const;

export const KV_TTL_SECONDS = {
  oauthState: 600,
  session: 1800,
  /**
   * Bezczynność, po której pracownik musi zalogować się ponownie.
   * Odświeżane przy każdym żądaniu; górny limit trwania sesji wynika
   * z `exp` w JWT i nie jest przedłużany.
   */
  staffSession: 7200,
  managedRulesets: 86400,
} as const;

export type KvPrefixKey = keyof typeof KV_PREFIX;

export function kvKey(prefix: KvPrefixKey, id: string): string {
  return `${KV_PREFIX[prefix]}${id}`;
}

/** Stan przepływu OAuth. TTL 600 s. */
export const oauthStateSchema = z.object({
  shareLinkToken: z.string(),
  codeVerifier: z.string(),
  createdAt: z.string(),
});
export type OauthState = z.infer<typeof oauthStateSchema>;

/** Sesja klienta po autoryzacji. TTL 1800 s. Token NIGDY nie trafia do D1 ani logów. */
export const sessionSchema = z.object({
  accessToken: z.string(),
  expiresAt: z.string(),
  shareLinkToken: z.string(),
  cfAccountIds: z.array(z.string()).default([]),
});
export type Session = z.infer<typeof sessionSchema>;

/**
 * Sesja pracownika. Kluczowa właściwość: JWT jest ważny tylko wtedy, gdy
 * TEN wpis istnieje. Skasowanie go unieważnia dostęp natychmiast, bez
 * czekania na wygaśnięcie tokena — to jest cały powód, dla którego sesja
 * w ogóle jest po stronie serwera.
 */
export const staffSessionSchema = z.object({
  userId: z.string(),
  email: z.string(),
  role: z.enum(['admin', 'support']),
  createdAt: z.string(),
  /** Bezwzględny koniec sesji. Nie przesuwa się wraz z aktywnością. */
  absoluteExpiresAt: z.string(),
});
export type StaffSession = z.infer<typeof staffSessionSchema>;

/**
 * Minimalny kontrakt KV — shared nie zależy od typów Workers, żeby dało się
 * go zaimportować także w buildzie Vite.
 */
export interface KvLike {
  get(key: string): Promise<string | null>;
  put(key: string, value: string, options?: { expirationTtl?: number }): Promise<void>;
  delete(key: string): Promise<void>;
}

export async function kvGet<T>(
  kv: KvLike,
  prefix: KvPrefixKey,
  id: string,
  schema: z.ZodType<T>,
): Promise<T | null> {
  const raw = await kv.get(kvKey(prefix, id));
  if (raw === null) return null;
  const parsed = schema.safeParse(JSON.parse(raw) as unknown);
  return parsed.success ? parsed.data : null;
}

export async function kvPut<T>(
  kv: KvLike,
  prefix: KvPrefixKey,
  id: string,
  value: T,
  ttlSeconds: number = KV_TTL_SECONDS[prefix],
): Promise<void> {
  await kv.put(kvKey(prefix, id), JSON.stringify(value), { expirationTtl: ttlSeconds });
}

export async function kvDelete(kv: KvLike, prefix: KvPrefixKey, id: string): Promise<void> {
  await kv.delete(kvKey(prefix, id));
}
